import React, { useMemo, useRef, useState } from "react";

import { Box, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { useQueryClient } from "@tanstack/react-query";

import { useTranslation } from "react-i18next";

import {
  DeckCardThumbnailRecord,
  getDeckCardDisplayData,
} from "@/cards/common/types/app-deck/deckCardThumbnail.types";

import { useAuth } from "@/shared/stores/useAuth";
import { useCurrentTalentProfile } from "@/shared";

import { CARD_ASPECT_RATIO_CSS } from "@/shared/constants/cardLayout";

import useThemeStore from "@/shared/stores/useThemeStore";

import DeleteCardConfirmDialog from "../modal/DeleteCardConfirmDialog";
import EditTaasCardDialog from "../modal/EditTaasCardDialog";

//import DeckCardFrontFace from "./DeckCardExpand/DeckCardFrontFace";
import DeckCardFrontFace from "@/cards/common/component/deck-library/DeckCardExpand/DeckCardFrontFace";
import { isImpactCardType } from "@/cards/impact-card/utils/impactCardType";
import { isInvitedImpactPartnerViewer, readImpactLocalPartner } from "@/cards/impact-card/utils/impactCardData";

import DeckCardlimitedEditionFrontFaceupdated from "@/modules/limited-edition/DeckCardlimitedEditionFrontFaceupdated";

/*---------------------------------------------------------------------------- */
/*Types                                                                       */
/* -------------------------------------------------------------------------- */

export type DeckCardOpenOptions = {
  isLimitedEdition: boolean;
  initialFace: "front" | "back";
};

type DeckCardThumbnailTileProps = {
  
  card: any;

  thumbnail?: DeckCardThumbnailRecord | null;

  isThumbnailLoading?: boolean;

  onClick: (options?: DeckCardOpenOptions) => void;

  canManage?: boolean;

  onCardMutated?: () => void;

  showTapHint?: boolean;

  showUpdateGlow?: boolean;

  updateTagLabel?: string;

  /**
   * Optional border flag.
   *
   * Normal cards do NOT receive the
   * Limited Edition border.
   */
  addBorder?: boolean;

  /**
   * Purchase record ID.
   *
   * Used for Limited Edition QR verification.
   */
  purchaseId?: number | string | null;

  /**
   * Actual purchased edition.
   *
   * Example:
   *
   * 2 -> #002
   */
  editionNumber?: number | string | null;
};

/* -------------------------------------------------------------------------- */
/* Card type                                                                  */
/* -------------------------------------------------------------------------- */

const getCardTypeLabel = (type?: string | null) => {
  const normalized = String(type ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  const labels: Record<string, string> = {
    single_card: "SINGLE ALBUM CARD",
    season_card: "SEASON CARD",
    demo_card: "DEMO CARD",
    impact_card: "IMPACT CARD",
    impact: "IMPACT CARD",
  };

  return labels[normalized] ?? normalized;
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const DeckCardThumbnailTile: React.FC<DeckCardThumbnailTileProps> = ({
  card,
  thumbnail,
  isThumbnailLoading = false,
  onClick,
  canManage,
  onCardMutated,
  showTapHint = true,
  showUpdateGlow = false,
  updateTagLabel,
  addBorder = false,
  purchaseId,
  editionNumber,
}) => {
  const { t } = useTranslation("taascard");

  const { darkMode } = useThemeStore();

  const { userInfo } = useAuth();
  const { currentProfile } = useCurrentTalentProfile();

  const queryClient = useQueryClient();

  const containerRef = useRef<HTMLDivElement>(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const [isEditOpen, setIsEditOpen] = useState(false);

  /* ------------------------------------------------------------------------ */
  /* Card display data                                                        */
  /* ------------------------------------------------------------------------ */

  /**
   * Tile hero art: `getDeckCardDisplayData().imageUrl`
   * (= `content.thumbnail` → `cover_image_url` / `coverImageUrl`).
   * Kakao share uses `getDeckCardCoverImageUrl` (cover first) — see `[Kakao share]` console logs.
   */
  const display = useMemo(() => getDeckCardDisplayData(thumbnail, card), [thumbnail, card]);

  /* ------------------------------------------------------------------------ */
  /* Card type label                                                          */
  /* ------------------------------------------------------------------------ */

  /**
   * Prefer thumbnail.type because that is normally
   * the API card type:
   *
   * Single_Card
   * Season_Card
   * Demo_Card
   * Impact_Card
   *
   * Fall back to the card itself.
   */
  const resolvedCardType = thumbnail?.type ?? card?.type ?? card?.card_type;
  const cardTypeLabel = getCardTypeLabel(resolvedCardType);
  const isImpactCard = isImpactCardType(resolvedCardType);

  /* ------------------------------------------------------------------------ */
  /* Limited Edition detection                                                */
  /* ------------------------------------------------------------------------ */

  const limited_edition =
    thumbnail?.limited_edition ??
    (
      card as {
        limited_edition?: number | null;
      } | null
    )?.limited_edition;

  const isLimitedEdition =
    limited_edition !== null && limited_edition !== undefined && Number(limited_edition) > 0;

  /* ------------------------------------------------------------------------ */
  /* Purchase information                                                     */
  /* ------------------------------------------------------------------------ */

  const thumbnailPurchase = thumbnail?.purchase as
    | {
        id?: number | string | null;

        edition?: number | string | null;
      }
    | null
    | undefined;

  const cardPurchase = (
    card as {
      purchase?: {
        id?: number | string | null;

        edition?: number | string | null;
      } | null;

      purchase_id?: number | string | null;

      edition?: number | string | null;
    } | null
  )?.purchase;

  /**
   * Purchase ID priority:
   *
   * 1. Explicit prop
   * 2. thumbnail.purchase.id
   * 3. card.purchase.id
   * 4. card.purchase_id
   */
  const resolvedPurchaseId =
    purchaseId ??
    thumbnailPurchase?.id ??
    cardPurchase?.id ??
    (
      card as {
        purchase_id?: number | string | null;
      } | null
    )?.purchase_id ??
    null;

  /**
   * Actual purchased edition.
   *
   * Do NOT use limitedEdition as an edition-number fallback.
   */
  const resolvedEditionNumber =
    editionNumber ??
    thumbnailPurchase?.edition ??
    cardPurchase?.edition ??
    (
      card as {
        edition?: number | string | null;
      } | null
    )?.edition ??
    null;

  const hasPurchaseId =
    resolvedPurchaseId !== null &&
    resolvedPurchaseId !== undefined &&
    String(resolvedPurchaseId).trim() !== "" &&
    Number(resolvedPurchaseId) > 0;

  /* ------------------------------------------------------------------------ */
  /* Ownership                                                                */
  /* ------------------------------------------------------------------------ */

  const cardTalentId =
    thumbnail?.talent_id ??
    thumbnail?.talent?.id ??
    (
      card as {
        talent?: {
          id?: number | string;
        };
      } | null
    )?.talent?.id ??
    (
      card as {
        talent_id?: number | string;
      } | null
    )?.talent_id;

  const isOwner =
    !!currentProfile?.id &&
    cardTalentId != null &&
    String(currentProfile.id) === String(cardTalentId);

  const showActions = canManage ?? isOwner;

  const impactViewerRole = isImpactCard
    ? isOwner
      ? "creator"
      : isInvitedImpactPartnerViewer(
          readImpactLocalPartner({
            partner: thumbnail?.partner ?? (card as { partner?: unknown } | null)?.partner,
            partner_id:
              thumbnail?.partner_id ??
              (card as { partner_id?: number } | null)?.partner_id,
          }),
          [userInfo?.id, currentProfile?.id]
        )
        ? "partner"
        : hasPurchaseId
          ? "contributor"
          : null
    : null;

  /* ------------------------------------------------------------------------ */
  /* Sealed                                                                   */
  /* ------------------------------------------------------------------------ */

  const isSealed =
    String(
      (
        card as {
          card_status?: string;
        }
      )?.card_status ?? ""
    ).toLowerCase() === "sealed";

  /* ------------------------------------------------------------------------ */
  /* Query invalidation                                                       */
  /* ------------------------------------------------------------------------ */

  const invalidateDeckLists = () => {
    queryClient.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) &&
        typeof q.queryKey[0] === "string" &&
        q.queryKey[0].startsWith("deck-card-thumbnails"),
    });

    onCardMutated?.();
  };

  /* ------------------------------------------------------------------------ */
  /* Open card                                                                */
  /* ------------------------------------------------------------------------ */

  const handleCardOpen = () => {
    if (isLimitedEdition) {
      onClick({
        isLimitedEdition: true,

        initialFace: "front",
      });

      return;
    }

    onClick({
      isLimitedEdition: false,

      initialFace: "front",
    });
  };

  /* ------------------------------------------------------------------------ */
  /* Actions                                                                  */
  /* ------------------------------------------------------------------------ */

  const handleViewClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    handleCardOpen();
  };

  const handleEditClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    setIsEditOpen(true);
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    setIsDeleteOpen(true);
  };

  /* ------------------------------------------------------------------------ */
  /* Loading                                                                  */
  /* ------------------------------------------------------------------------ */

  if (isThumbnailLoading && !display.imageUrl) {
    return (
      <Box
        ref={containerRef}
        sx={{
          width: "100%",

          minWidth: 0,
        }}
      >
        <Skeleton
          variant="rounded"
          sx={{
            width: "100%",

            aspectRatio: CARD_ASPECT_RATIO_CSS,

            borderRadius: {
              xs: "12px",

              sm: "16px",

              md: "18px",
            },
          }}
        />
      </Box>
    );
  }

  /* ------------------------------------------------------------------------ */
  /* Manage action styling                                                    */
  /* ------------------------------------------------------------------------ */

  const baseCircle = {
    width: {
      xs: 32,
      sm: 40,
    },

    height: {
      xs: 32,
      sm: 40,
    },

    borderRadius: "50%",

    background: darkMode ? "rgba(13,12,10,0.92)" : "rgba(0,0,0,0.72)",

    border: "1px solid rgba(255,255,255,0.24)",

    backdropFilter: "blur(12px)",

    boxShadow: "0 6px 16px rgba(0,0,0,0.5)",

    transition:
      "transform 0.18s ease, background 0.18s ease, color 0.18s ease, border-color 0.18s ease",

    "&:hover": {
      transform: "scale(1.08)",
    },

    "&:active": {
      transform: "scale(0.92)",
    },
  } as const;

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",

        flexDirection: "column",

        alignItems: "stretch",

        width: "100%",

        minWidth: 0,
      }}
    >
      {/* ------------------------------------------------------------------ */}
      {/* Clickable card                                                     */}
      {/* ------------------------------------------------------------------ */}

      <Box
        onClick={handleCardOpen}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();

            handleCardOpen();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={t("deck_open_card")}
        sx={{
          position: "relative",

          width: "100%",

          cursor: "pointer",

          outline: "none",

          borderRadius: {
            xs: "12px",

            sm: "16px",

            md: "18px",
          },

          transition: "transform 0.2s ease, box-shadow 0.2s ease",

          ...(showUpdateGlow
            ? {
                boxShadow:
                  "0 0 1px 2px rgba(255, 220, 40, 0.95), 0 0 14px 4px rgba(255, 210, 20, 0.52), 0 0 1px 3px rgba(255, 200, 0, 0.3)",
              }
            : null),

          "&:hover": {
            transform: "translateY(-4px)",
          },

          "&:focus-visible": {
            boxShadow: "0 0 0 3px var(--taas-primary, #14b8a6)",
          },

          "&:hover .deck-tile-actions": {
            opacity: 1,

            pointerEvents: "auto",
          },
        }}
      >
        {/* -------------------------------------------------------------- */}
        {/* Card face                                                      */}
        {/* -------------------------------------------------------------- */}

        {isLimitedEdition ? (
          <DeckCardlimitedEditionFrontFaceupdated
            totalEdition={thumbnail?.limited_edition?.toString() ?? "0"}
            compact
            talentName={
              thumbnail?.talent?.view_name ??
              thumbnail?.talent?.username ??
              card?.talent?.view_name ??
              card?.talent?.username ??
              "TAAS"
            }
            title={display.title ?? card?.title ?? "Limited Edition"}
            imageUrl={display.imageUrl}
            editionNumber={resolvedEditionNumber}
            purchaseId={hasPurchaseId ? resolvedPurchaseId : null}
            cardType={cardTypeLabel}
            addBorder={addBorder}
          />
        ) : (
          <DeckCardFrontFace
            display={{
              ...display,
              typeLabel: isImpactCard ? "IMPACT CARD" : display.typeLabel,
            }}
            compact
            isSealed={isSealed}
            viewerRole={impactViewerRole}
            purchaseId={hasPurchaseId ? resolvedPurchaseId : null}
            addBorder={false}
          />
        )}
        {/* -------------------------------------------------------------- */}
        {/* Grown / update tag                                             */}
        {/* -------------------------------------------------------------- */}

        {Boolean(updateTagLabel) && (
          <Box
            sx={{
              position: "absolute",

              top: 8,

              right: 13,

              zIndex: 5,

              px: {
                xs: 0.85,
                sm: 1,
              },

              py: {
                xs: 0.28,
                sm: 0.34,
              },

              borderRadius: "999px",

              bgcolor: "rgba(245, 230, 190, 0.96)",

              border: "1px solid rgba(201, 164, 76,0.65)",

              boxShadow: "0 2px 8px rgba(0,0,0,0.28)",

              pointerEvents: "none",
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: "0.5rem",

                  sm: "0.54rem",
                },

                fontWeight: 800,

                letterSpacing: "0.08em",

                textTransform: "uppercase",

                color: "#1a1410",

                fontFamily: '"Lexend", sans-serif',

                lineHeight: 1,
              }}
            >
              {updateTagLabel}
            </Typography>
          </Box>
        )}

        {/* -------------------------------------------------------------- */}
        {/* Manage cluster                                                 */}
        {/* -------------------------------------------------------------- */}

        {showActions && (
          <Box
            className="deck-tile-actions"
            sx={{
              position: "absolute",

              top: "50%",

              left: "50%",

              transform: "translate(-50%, -50%)",

              zIndex: 6,

              display: "flex",

              alignItems: "center",

              gap: {
                xs: 0.75,
                sm: 1.25,
              },

              opacity: {
                xs: 1,
                sm: 0,
              },

              pointerEvents: {
                xs: "auto",

                sm: "none",
              },

              transition: "opacity 0.22s ease",
            }}
          >
            {/* View */}

            <Tooltip title={t("view")} arrow>
              <IconButton
                size="small"
                onClick={handleViewClick}
                aria-label={t("view")}
                sx={{
                  ...baseCircle,

                  color: "#fff",

                  "&:hover": {
                    ...baseCircle["&:hover"],

                    color: "#fff",

                    background: "rgba(79,70,229,0.92)",

                    borderColor: "rgba(129,140,248,0.6)",
                  },
                }}
              >
                <VisibilityIcon
                  sx={{
                    fontSize: {
                      xs: "1rem",

                      sm: "1.15rem",
                    },
                  }}
                />
              </IconButton>
            </Tooltip>

            {/* Delete */}

            {!isSealed && (
              <Tooltip title={t("delete")} arrow>
                <IconButton
                  size="small"
                  onClick={handleDeleteClick}
                  aria-label={t("delete")}
                  sx={{
                    ...baseCircle,

                    color: "rgba(252,165,165,0.95)",

                    "&:hover": {
                      ...baseCircle["&:hover"],

                      color: "#fff",

                      background: "rgba(239,68,68,0.9)",

                      borderColor: "rgba(239,68,68,0.7)",
                    },
                  }}
                >
                  <DeleteOutlineIcon
                    sx={{
                      fontSize: {
                        xs: "1rem",

                        sm: "1.15rem",
                      },
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Tap to open                                                        */}
      {/* ------------------------------------------------------------------ */}

      {/* <Typography
        sx={{
          mt: {
            xs: 0.75,
            sm: 1,
          },

          fontSize: {
            xs: "0.78rem",

            sm: "0.82rem",
          },

          color: "var(--neutral-500)",

          display: showTapHint ? "flex" : "none",

          alignItems: "center",

          justifyContent: "center",

          gap: 0.5,
        }}
      >
        ↗ {t("deck_tap_to_open")}
      </Typography> */}

      {/* ------------------------------------------------------------------ */}
      {/* Delete confirmation                                                */}
      {/* ------------------------------------------------------------------ */}

      {showActions && (
        <DeleteCardConfirmDialog
          open={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          cardId={card.id}
          cardTitle={card.title}
          onDeleted={invalidateDeckLists}
        />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Edit dialog                                                        */}
      {/* ------------------------------------------------------------------ */}

      {showActions && (
        <Box onClick={(event) => event.stopPropagation()}>
          <EditTaasCardDialog
            open={isEditOpen}
            onClose={() => setIsEditOpen(false)}
            card={card}
            onSaved={invalidateDeckLists}
          />
        </Box>
      )}
    </Box>
  );
};

export default DeckCardThumbnailTile;
